import { useState, useCallback } from "react";

/**
 * Custom hook for multi-step form validation
 * Reduces code duplication across program application forms
 * 
 * @param {Object} formData - Current form data state
 * @param {Object} config - Validation configuration
 * @param {Object} config.requiredFieldsByStep - Object mapping step numbers to arrays of required field names
 * @param {Object} config.fieldLabels - Object mapping field names to human-readable labels
 * @param {Array} config.emailFields - Array of field names that should be validated as emails
 * @param {Object} config.customValidators - Object mapping field names to custom validation functions
 * @returns {Object} - Validation functions and error state
 */
export const useFormValidation = (formData, config) => {
  const [validationErrors, setValidationErrors] = useState({});

  const { requiredFieldsByStep = {}, fieldLabels = {}, emailFields = [], customValidators = {} } = config || {};

  // Email regex pattern
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /**
   * Validate a single step
   * @param {number} step - Step number to validate
   * @returns {boolean} - Whether the step is valid
   */
  const validateStep = useCallback((step) => {
    const errors = {};
    const fieldsToValidate = requiredFieldsByStep[step] || [];
    
    fieldsToValidate.forEach((field) => {
      const value = formData[field];
      if (!value || (typeof value === 'string' && value.trim() === "")) {
        errors[field] = `${fieldLabels[field] || field} is required`;
      }
    });

    // Email validation for specified email fields
    emailFields.forEach((field) => {
      if (formData[field] && !emailPattern.test(formData[field])) {
        errors[field] = "Please enter a valid email address";
      }
    });

    // Run custom validators for this step's fields
    fieldsToValidate.forEach((field) => {
      if (customValidators[field]) {
        const customError = customValidators[field](formData[field], formData);
        if (customError) {
          errors[field] = customError;
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [requiredFieldsByStep, fieldLabels, formData, emailFields, customValidators]);

  /**
   * Validate all steps at once (for final submission)
   * @returns {boolean} - Whether all steps are valid
   */
  const validateAllSteps = useCallback(() => {
    const errors = {};
    
    Object.values(requiredFieldsByStep).flat().forEach((field) => {
      const value = formData[field];
      if (!value || (typeof value === 'string' && value.trim() === "")) {
        errors[field] = `${fieldLabels[field] || field} is required`;
      }
    });

    // Email validation for all email fields
    emailFields.forEach((field) => {
      if (formData[field] && !emailPattern.test(formData[field])) {
        errors[field] = "Please enter a valid email address";
      }
    });

    // Run all custom validators
    Object.keys(customValidators).forEach((field) => {
      const customError = customValidators[field](formData[field], formData);
      if (customError) {
        errors[field] = customError;
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [requiredFieldsByStep, fieldLabels, formData, emailFields, customValidators]);

  /**
   * Clear a specific field's error when user updates it
   * @param {string} field - Field name to clear error for
   */
  const clearFieldError = useCallback((field) => {
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [validationErrors]);

  /**
   * Check if a specific field has an error
   * @param {string} field - Field name to check
   * @returns {boolean}
   */
  const hasError = useCallback((field) => {
    return !!validationErrors[field];
  }, [validationErrors]);

  /**
   * Get error message for a specific field
   * @param {string} field - Field name
   * @returns {string|null}
   */
  const getError = useCallback((field) => {
    return validationErrors[field] || null;
  }, [validationErrors]);

  /**
   * Reset all validation errors
   */
  const resetErrors = useCallback(() => {
    setValidationErrors({});
  }, []);

  return {
    validationErrors,
    validateStep,
    validateAllSteps,
    clearFieldError,
    hasError,
    getError,
    resetErrors,
    setValidationErrors
  };
};

export default useFormValidation;
